import { Group } from "@fairfleet/geotab";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormPanelProps } from "./formFields";
import { ROOT_SECURITY_GROUP_ID } from "@/constants/geotabGroupIds";
import { useGroupPickerHint } from "@/hooks/groups/useGroupPickerHint";
import { groupFilterOptions } from "@/utilities/groupFilterOptions";

interface Props extends Pick<FormPanelProps, "form" | "setField"> {
  /** Security groups the selected user currently has, resolved against the loaded group tree. */
  selectedUserSecurityGroups: Group[];
  /** All known clearances, sorted by name. */
  securityGroupsSorted: Group[];
  assignableClearanceIds: Set<string>;
  companyGroupOptions: Group[];
  roleDisabled: boolean;
  onRoleChange: () => void;
}

const byName = (a: Group, b: Group) => (a.name ?? a.id).localeCompare(b.name ?? b.id);

export function ClearancesTab({
  form,
  setField,
  selectedUserSecurityGroups,
  securityGroupsSorted,
  assignableClearanceIds,
  companyGroupOptions,
  roleDisabled,
  onRoleChange,
}: Props) {
  const { t } = useTranslation();
  const { selectedClearanceId } = form;
  const groupPickerHint = useGroupPickerHint(companyGroupOptions.length);

  const assignableSecurityGroups = useMemo(() => {
    const currentAssignedGroup =
      form.securityGroups.find(group => group.id === selectedClearanceId) ??
      selectedUserSecurityGroups.find(group => group.id === selectedClearanceId) ??
      null;
    const options = securityGroupsSorted.filter(
      group => group.id !== ROOT_SECURITY_GROUP_ID && assignableClearanceIds.has(group.id),
    );
    if (!currentAssignedGroup || options.some(group => group.id === currentAssignedGroup.id)) {
      return options;
    }
    return [currentAssignedGroup, ...options].sort(byName);
  }, [
    form.securityGroups,
    securityGroupsSorted,
    selectedClearanceId,
    selectedUserSecurityGroups,
    assignableClearanceIds,
  ]);

  const selectedClearance =
    selectedUserSecurityGroups.find(group => group.id === selectedClearanceId) ??
    securityGroupsSorted.find(group => group.id === selectedClearanceId) ??
    null;

  const currentRoleNames = selectedUserSecurityGroups
    .map(group => group.name ?? group.id)
    .join(", ");

  return (
    <Stack spacing={1.25}>
      <TextField
        label={t("users.currentRoles")}
        value={currentRoleNames}
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
        helperText={t("users.currentRolesHint")}
      />

      <TextField
        label={t("users.clearance.assignedRole")}
        value={selectedClearanceId ?? ""}
        onChange={event => {
          const nextId = event.target.value;
          const nextGroup = assignableSecurityGroups.find(group => group.id === nextId) ?? null;
          setField("selectedClearanceId", nextId || null);
          setField("securityGroups", nextGroup ? [nextGroup] : []);
          onRoleChange();
        }}
        fullWidth
        size="small"
        select
        disabled={roleDisabled}
      >
        {assignableSecurityGroups.map(group => (
          <MenuItem key={group.id} value={group.id}>
            {group.name ?? group.id}
          </MenuItem>
        ))}
      </TextField>

      {selectedClearance?.comments ? (
        <TextField
          label={t("users.clearance.comment")}
          value={selectedClearance.comments}
          fullWidth
          size="small"
          multiline
          minRows={3}
          InputProps={{ readOnly: true }}
        />
      ) : null}

      <Autocomplete
        multiple
        options={companyGroupOptions}
        value={form.companyGroups}
        onChange={(_event, value) => setField("companyGroups", value)}
        getOptionLabel={option => option.name ?? option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={groupFilterOptions}
        renderInput={params => (
          <TextField
            {...params}
            label={t("users.companyGroups")}
            size="small"
            helperText={groupPickerHint}
          />
        )}
      />

      <Autocomplete
        multiple
        options={companyGroupOptions}
        value={form.reportGroups}
        onChange={(_event, value) => setField("reportGroups", value)}
        getOptionLabel={option => option.name ?? option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={groupFilterOptions}
        renderInput={params => (
          <TextField
            {...params}
            label={t("users.reportGroups")}
            size="small"
            helperText={groupPickerHint}
          />
        )}
      />

      <Typography variant="body2" color="text.secondary">
        {t("users.supportHint")}
      </Typography>
    </Stack>
  );
}
